import * as THREE from 'three';

/**
 * The running cushion count, popped at the rail where each contact happened.
 *
 * Only used in 마지막 3쿠션 (the final-shot stage), where the shot is *about* the
 * cushion count — the player needs N rails before the second ball, so showing the
 * tally land one-by-one at each rail turns the tense shot into a visible run.
 *
 * Each pop is a camera-facing sprite drawn over everything (depthTest off), so a
 * number never disappears inside a rail or under the table. Sprites are built
 * once per digit and reused; a shot can only produce a handful at a time.
 *
 * Z-up world, cloth at z=0 (matching TablePointers).
 */

/** How long one pop lives, seconds. */
const LIFE = 1.6;
/** How far it floats up over its life, metres. */
const RISE = 0.22;
/** Height above the cloth a pop starts at. */
const START_Z = 0.06;

interface Pop {
  sprite: THREE.Sprite;
  age: number;
  /** Reached the required count: bigger, gold, lingers a little longer. */
  win: boolean;
}

export class CushionPops {
  readonly group = new THREE.Group();
  private readonly pool: Pop[] = [];
  private readonly textures = new Map<string, THREE.Texture>();

  /** Live pops right now — the frame loop keeps rendering while this is > 0. */
  get count(): number {
    return this.pool.reduce((n, p) => n + (p.sprite.visible ? 1 : 0), 0);
  }

  /**
   * Draw the number onto a canvas once and cache it. Sized generously so the
   * texture stays crisp when a winning pop scales up.
   */
  private texture(label: string, win: boolean): THREE.Texture {
    const key = `${label}|${win}`;
    const hit = this.textures.get(key);
    if (hit) return hit;

    const S = 256;
    const cv = document.createElement('canvas');
    cv.width = cv.height = S;
    const g = cv.getContext('2d')!;
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.font = `bold ${win ? 128 : 150}px system-ui, sans-serif`;

    // outline first, then fill — keeps the number legible against felt OR wood
    g.lineWidth = 14;
    g.strokeStyle = 'rgba(0,0,0,0.85)';
    g.strokeText(label, S / 2, S / 2);
    const grad = g.createLinearGradient(0, S * 0.3, 0, S * 0.7);
    if (win) {
      grad.addColorStop(0, '#fff6c2');
      grad.addColorStop(0.5, '#ffd24a');
      grad.addColorStop(1, '#ff9d2e'); // gold, for the count that wins it
    } else {
      grad.addColorStop(0, '#ffffff');
      grad.addColorStop(1, '#bfe4ff');
    }
    g.fillStyle = grad;
    g.fillText(label, S / 2, S / 2);

    const tex = new THREE.CanvasTexture(cv);
    tex.colorSpace = THREE.SRGBColorSpace;
    this.textures.set(key, tex);
    return tex;
  }

  private take(): Pop {
    const free = this.pool.find((p) => !p.sprite.visible);
    if (free) return free;
    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ transparent: true, depthTest: false, depthWrite: false })
    );
    sprite.renderOrder = 1000; // over the balls, rails and pointers
    this.group.add(sprite);
    const pop: Pop = { sprite, age: 0, win: false };
    this.pool.push(pop);
    return pop;
  }

  /**
   * Pop `label` at table position (x, y). `win` marks the count that satisfies
   * the rule, which gets the gold treatment.
   */
  show(x: number, y: number, label: string, win: boolean): void {
    const pop = this.take();
    const mat = pop.sprite.material as THREE.SpriteMaterial;
    mat.map = this.texture(label, win);
    mat.needsUpdate = true;
    pop.age = 0;
    pop.win = win;
    pop.sprite.position.set(x, y, START_Z);
    pop.sprite.visible = true;
    this.update(0); // apply the t=0 scale immediately so it never flashes full-size
  }

  /** Advance every live pop; hide the ones that have finished. */
  update(dt: number): void {
    for (const p of this.pool) {
      if (!p.sprite.visible) continue;
      p.age += dt;
      const life = p.win ? LIFE * 1.4 : LIFE;
      const t = p.age / life;
      if (t >= 1) {
        p.sprite.visible = false;
        continue;
      }
      // overshoot on entry (a "pop"), settle, then drift up and fade out
      const grow = t < 0.18 ? 1.35 - 0.35 * Math.cos((t / 0.18) * Math.PI) : 1;
      const base = (p.win ? 0.20 : 0.14) * grow;
      p.sprite.scale.set(base, base, 1);
      p.sprite.position.z = START_Z + RISE * t * t; // eases upward
      (p.sprite.material as THREE.SpriteMaterial).opacity = t > 0.65 ? 1 - (t - 0.65) / 0.35 : 1;
    }
  }

  /** Hide everything at once (shot aborted, scene torn down, turn moved on). */
  clear(): void {
    for (const p of this.pool) p.sprite.visible = false;
  }

  dispose(): void {
    for (const p of this.pool) {
      p.sprite.material.dispose();
      this.group.remove(p.sprite);
    }
    this.pool.length = 0;
    for (const t of this.textures.values()) t.dispose();
    this.textures.clear();
  }
}
